import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import {
    LayoutDashboard,
    Briefcase,
    Coins,
    ArrowLeftRight,
    LineChart,
    Bell,
    FileText,
    Settings as SettingsIcon,
    CreditCard,
    LifeBuoy,
    Shield,
    LogOut,
    Search,
    Gift,
    Landmark,
    Atom,
    Activity,
    Wallet,
    Upload,
    Menu,
    X,
    BookOpen,
    Megaphone,
    Newspaper,
    Send,
    Tag,
    Users,
    Link2,
    Building2,
    ChevronDown,
} from "lucide-react";

import Logo from "@/components/brand/Logo";
import DisclaimerBar from "@/components/brand/DisclaimerBar";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import storage from "@/lib/storage";
import { isNative } from "@/lib/appConfig";
import { hapticTap } from "@/lib/haptics";
import BottomNav from "@/components/layout/BottomNav";
import usePlan from "@/hooks/usePlan";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import TrialBanner from "@/components/billing/TrialBanner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import useEngine from "@/hooks/useEngine";
import useAppLanguage from "@/i18n/useAppLanguage";
import { engineLabelLang, isEngineRoute, engineShellPending } from "@/i18n/languages";
import { useTranslation } from "react-i18next";
import { ENGINE_NS, useCatalogsReady } from "@/i18n/lazyCatalogs";
import LanguageSelect from "@/components/engine/LanguageSelect";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { appTitle } from "@/lib/documentTitle";
import { showEngineChrome } from "@/lib/engineFunnel";

const navItems = [
    { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/app/portfolios", label: "Carteras", icon: Briefcase, testid: "nav-portfolios" },
    { to: "/app/holdings", label: "Activos", icon: Coins, testid: "nav-holdings" },
    { to: "/app/connections", label: "Conectar broker", icon: Link2, testid: "nav-connections", badge: "Nuevo" },
    { to: "/app/transactions", label: "Transacciones", icon: ArrowLeftRight, testid: "nav-transactions" },
    { to: "/app/dividends", label: "Dividendos", icon: Wallet, testid: "nav-dividends", requiredPlan: "Plus" },
    { to: "/app/benchmarks", label: "Benchmarks", icon: LineChart, testid: "nav-benchmarks", requiredPlan: "Plus" },
    { to: "/app/alerts", label: "Alertas", icon: Bell, testid: "nav-alerts", requiredPlan: "Plus" },
    { to: "/app/riesgo", label: "Riesgo", icon: Activity, testid: "nav-risk", requiredPlan: "Wealth" },
    { to: "/app/optimizar", label: "Optimización cuántica", icon: Atom, testid: "nav-optimize", requiredPlan: "Pro" },
    { to: "/app/fiscal", label: "Fiscal ES", icon: Landmark, testid: "nav-fiscal", requiredPlan: "Pro" },
    { to: "/app/reports", label: "Informes", icon: FileText, testid: "nav-reports", requiredPlan: "Pro" },
    { to: "/app/referrals", label: "Referidos", icon: Gift, testid: "nav-referrals" },
    { to: "/app/import", label: "Importar CSV", icon: Upload, testid: "nav-import", requiredPlan: "Pro" },
    { to: "/blog", label: "Blog", icon: BookOpen, testid: "nav-blog", newTab: true },
];

const ENGINE_ITEM = { to: "/app/engine", labelKey: "shell.name", label: "QCapital Engine", icon: Building2, testid: "nav-engine" };
const PERSONAL_PATHS = navItems.filter((it) => !it.newTab).map((it) => it.to);

const secondaryItems = [
    { to: "/app/settings", label: "Ajustes", icon: SettingsIcon, testid: "nav-settings" },
    { to: "/app/billing", label: "Facturación", icon: CreditCard, testid: "nav-billing" },
    { to: "/app/help", label: "Ayuda y soporte", icon: LifeBuoy, testid: "nav-help" },
];

// Con la cabecera del Engine: sin la facturacion de particulares y una sola ayuda, la del Engine.
const engineSecondaryItems = [
    { to: "/app/settings", labelKey: "shell.settings", label: "Ajustes", icon: SettingsIcon, testid: "nav-settings" },
    { to: "/app/engine/ayuda", labelKey: "shell.help", label: "Ayuda", icon: LifeBuoy, testid: "nav-help" },
];

export default function AppLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const userPlan = usePlan();
    const [portfolios, setPortfolios] = useState([]);
    const [portfoliosLoaded, setPortfoliosLoaded] = useState(false);
    const [selectedPortfolio, setSelectedPortfolio] = useState(storage.getItem("qc_portfolio") || "");
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
    const [adminPending, setAdminPending] = useState(0);
    const [notifications, setNotifications] = useState([]);

    useEffect(() => {
        if (user?.role !== "admin") return;
        const fetchPending = () => {
            api.get("/api/tickets/admin/stats")
                .then(({ data }) => setAdminPending((data.open || 0) + (data.in_progress || 0)))
                .catch(() => {});
        };
        fetchPending();
        const interval = setInterval(fetchPending, 60000);
        return () => clearInterval(interval);
    }, [user?.role]);

    const fetchNotifications = useCallback(async () => {
        try {
            const { data } = await api.get("/api/notifications");
            setNotifications(Array.isArray(data) ? data : []);
        } catch {
            // silently fail
        }
    }, []);

    useEffect(() => {
        fetchNotifications();
        const interval = setInterval(fetchNotifications, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, [fetchNotifications]);

    const unreadCount = notifications.filter((n) => !n.read).length;

    const markRead = async (id) => {
        try {
            await api.patch(`/api/notifications/${id}/read`);
            setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
        } catch {}
    };

    const markAllRead = async () => {
        const unread = notifications.filter((n) => !n.read);
        await Promise.allSettled(unread.map((n) => api.patch(`/api/notifications/${n.id}/read`)));
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    };

    const daysSince = (iso) => {
        const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
        return d === 0 ? "hoy" : `hace ${d} día${d === 1 ? "" : "s"}`;
    };

    const [announcements, setAnnouncements] = useState([]);

    useEffect(() => {
        api.get("/api/announcements/active")
            .then(({ data }) => setAnnouncements(Array.isArray(data) ? data : []))
            .catch(() => {});
    }, []);

    const announcement = announcements[0] || null;

    const dismissAnnouncement = async () => {
        if (!announcement) return;
        setAnnouncements((prev) => prev.slice(1));
        try { await api.post(`/api/announcements/${announcement.id}/dismiss`); } catch {}
    };

    const openUrl = (url) => {
        if (!url) return;
        if (url.startsWith("/")) navigate(url);
        else window.open(url, "_blank", "noopener");
    };

    useEffect(() => {
        api.get("/api/portfolios")
            .then((r) => {
                const list = r.data || [];
                setPortfolios(list);
                setPortfoliosLoaded(true);
                const isValid = list.some((p) => p.id === selectedPortfolio);
                if (!isValid) {
                    // Stored ID is stale (wrong user, deleted portfolio, or empty) — reset
                    const newId = list[0]?.id || "";
                    setSelectedPortfolio(newId);
                    if (newId) storage.setItem("qc_portfolio", newId);
                    else storage.removeItem("qc_portfolio");
                    window.dispatchEvent(new Event("qc_portfolio_change"));
                }
            })
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // AppLayout solo emitía el evento y nunca lo escuchaba, así que al entrar en el
    // detalle de otra cartera el selector se quedaba marcando la anterior.
    // También se resincroniza al cambiar de ruta: los efectos del hijo corren antes
    // que los del padre, así que en la carga directa de /portfolios/:id el evento
    // que emite el detalle se dispara antes de que este listener exista.
    useEffect(() => {
        const sync = () => setSelectedPortfolio(storage.getItem("qc_portfolio") || "");
        sync();
        window.addEventListener("qc_portfolio_change", sync);
        return () => window.removeEventListener("qc_portfolio_change", sync);
    }, [location.pathname]);

    const onPortfolioChange = (v) => {
        setSelectedPortfolio(v);
        storage.setItem("qc_portfolio", v);
        window.dispatchEvent(new Event("qc_portfolio_change"));
    };

    const closeDrawer = () => setMobileMenuOpen(false);

    // Let the Android back button close the drawer (NativeBackButton dispatches this).
    useEffect(() => {
        const close = () => setMobileMenuOpen(false);
        window.addEventListener("qc:closeDrawer", close);
        return () => window.removeEventListener("qc:closeDrawer", close);
    }, []);

    // Shared nav link class builder for sidebar (desktop) — unchanged style
    const desktopNavClass = ({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-lg text-[14.5px] transition-all ${
            isActive
                ? "bg-soft_border text-graphite font-medium"
                : "text-soft_muted hover:text-graphite hover:bg-soft_border/40"
        }`;

    // Nav link class builder for mobile drawer — left-border accent style, min 44px touch target
    const drawerNavClass = ({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 min-h-[44px] text-[14.5px] transition-all ${
            isActive
                ? "border-l-2 border-petrol bg-soft_border text-graphite font-medium"
                : "border-l-2 border-transparent text-soft_muted hover:text-graphite hover:bg-stone"
        }`;

    // Para miembros del Engine (y nunca en la app nativa, donde el Engine no se enlaza):
    // el Engine primero y la parte B2C agrupada en "Mi cartera personal" (decision D-B2).
    const engine = useEngine();
    const engineMember = engine.member && !isNative();
    // Esta capa es compartida con el B2C, que sigue en español: solo el bloque del Engine, en sus rutas, sigue el idioma activo.
    const { i18n } = useTranslation();
    const labelLang = engineLabelLang(engineMember, location.pathname, i18n.language);
    // Los textos del menu del Engine llegan con su catalogo (i18n/lazyCatalogs.js), que solo se pide para
    // un miembro: hasta tenerlo, el menu es el de particulares y las entradas con `label` usan ese literal.
    const shellReady = useCatalogsReady(ENGINE_NS, labelLang, engineMember);
    const engineMode = engineMember && shellReady;
    const engineT = i18n.getFixedT(labelLang, "engine");
    const itemLabel = (it) => (it.labelKey && shellReady ? engineT(it.labelKey) : it.label);
    const languageSettled = useAppLanguage({
        member: engineMember, user, orgLanguage: engine.me?.org?.language, pathname: location.pathname, engineLoading: engine.loading,
    });
    const shellPending = engineShellPending({
        pathname: location.pathname, engineLoading: engine.loading, member: engineMember, shellReady, languageSettled,
        engineError: engine.access.code === "error" && !isNative(),
    });
    const onPersonalRoute = PERSONAL_PATHS.some((p) => location.pathname.startsWith(p));
    // Cabecera del Engine (revision UX 26/10/2026): sin prueba de Pro, selector de cartera
    // personal, buscador de activos ni facturacion de particulares. Ver lib/engineFunnel.js.
    const personalCount = portfoliosLoaded ? portfolios.length : null;
    const engineChrome = showEngineChrome({
        member: engine.member, loading: engine.loading, native: isNative(),
        pathname: location.pathname, personalRoute: onPersonalRoute, personalCount,
    });
    // Un miembro sin carteras personales no va a comprar un plan de particulares: sin insignias.
    const quietPersonal = engineMode && personalCount === 0;
    const sideItems = engineChrome ? engineSecondaryItems : secondaryItems;
    useDocumentTitle(appTitle(location.pathname, shellReady ? engineT : null));
    const [personalOpen, setPersonalOpen] = useState(onPersonalRoute);
    useEffect(() => {
        if (onPersonalRoute) setPersonalOpen(true);
    }, [onPersonalRoute]);

    const drawerItem = (it) => it.newTab ? (
        <a
            key={it.to}
            href={it.to}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`mobile-${it.testid}`}
            className="flex items-center gap-3 px-3 py-2 min-h-[44px] text-[14.5px] transition-all border-l-2 border-transparent text-soft_muted hover:text-graphite hover:bg-stone"
            onClick={closeDrawer}
        >
            <it.icon className="w-4 h-4 shrink-0" strokeWidth={1.5} aria-hidden="true" />
            {itemLabel(it)}
        </a>
    ) : (
        <NavLink
            key={it.to}
            to={it.to}
            data-testid={`mobile-${it.testid}`}
            className={drawerNavClass}
            onClick={() => { hapticTap(); closeDrawer(); }}
        >
            <it.icon className="w-4 h-4 shrink-0" strokeWidth={1.5} aria-hidden="true" />
            {itemLabel(it)}
            {it.badge && (
                <span className="ml-auto text-[9px] font-bold bg-petrol text-bone rounded px-1.5 py-0.5 uppercase tracking-wide leading-none shrink-0">
                    {it.badge}
                </span>
            )}
            {userPlan === "free" && it.requiredPlan && !quietPersonal && (
                <span className="ml-auto text-[9px] font-bold bg-petrol/10 text-petrol border border-petrol/20 rounded px-1.5 py-0.5 uppercase tracking-wide leading-none shrink-0">
                    {it.requiredPlan}
                </span>
            )}
        </NavLink>
    );

    const desktopItem = (it) => it.newTab ? (
        <a
            key={it.to}
            href={it.to}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={it.testid}
            className="flex items-center gap-3 px-3 py-2 rounded-lg text-[14.5px] transition-all text-soft_muted hover:text-graphite hover:bg-soft_border/40"
        >
            <it.icon className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" />
            {itemLabel(it)}
        </a>
    ) : (
        <NavLink
            key={it.to}
            to={it.to}
            data-testid={it.testid}
            className={desktopNavClass}
        >
            <it.icon className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" />
            {itemLabel(it)}
            {it.badge && (
                <span className="ml-auto text-[9px] font-bold bg-petrol text-bone rounded px-1.5 py-0.5 uppercase tracking-wide leading-none shrink-0">
                    {it.badge}
                </span>
            )}
            {userPlan === "free" && it.requiredPlan && !quietPersonal && (
                <span className="ml-auto text-[9px] font-bold bg-petrol/10 text-petrol border border-petrol/20 rounded px-1.5 py-0.5 uppercase tracking-wide leading-none shrink-0">
                    {it.requiredPlan}
                </span>
            )}
        </NavLink>
    );

    return (
        <div className="min-h-screen bg-stone flex" data-testid="app-layout">

            {/* ── Mobile drawer backdrop ── */}
            <div
                className={`fixed inset-0 z-40 bg-black/40 transition-opacity duration-300 md:hidden ${
                    mobileMenuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
                }`}
                aria-hidden="true"
                onClick={closeDrawer}
            />

            {/* ── Mobile drawer panel ── */}
            <div
                className={`fixed top-0 left-0 z-50 h-full w-[280px] flex flex-col bg-stone shadow-xl transition-transform duration-300 ease-in-out md:hidden ${
                    mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
                }`}
                role="dialog"
                aria-label="Menú de navegación"
                aria-modal="true"
                data-testid="mobile-drawer"
                data-open={mobileMenuOpen ? "true" : "false"}
            >
                {/* Drawer header — brand + close button */}
                <div className="app-drawer-header flex items-center justify-between px-4 py-4 border-b border-soft_border">
                    <Logo size="md" />
                    <button
                        onClick={closeDrawer}
                        className="flex items-center justify-center w-11 h-11 rounded-md text-soft_muted hover:text-graphite hover:bg-soft_border transition-colors"
                        aria-label="Cerrar menú"
                        data-testid="mobile-drawer-close"
                    >
                        <X className="w-5 h-5" strokeWidth={1.5} aria-hidden="true" />
                    </button>
                </div>

                {/* Portfolio selector (mobile only — inside drawer) */}
                {!engineChrome && (
                <div className="px-4 py-3 border-b border-soft_border">
                    <p className="text-xs text-soft_muted mb-1.5 font-medium uppercase tracking-wide">Cartera activa</p>
                    <Select value={selectedPortfolio} onValueChange={onPortfolioChange}>
                        <SelectTrigger
                            className="bg-bone border-soft_border w-full"
                            data-testid="portfolio-selector-mobile"
                        >
                            <SelectValue placeholder="Selecciona cartera" />
                        </SelectTrigger>
                        <SelectContent>
                            {portfolios.length === 0 && (
                                <SelectItem value="none" disabled>
                                    No tienes carteras aún
                                </SelectItem>
                            )}
                            {portfolios.map((p) => (
                                <SelectItem key={p.id} value={p.id} data-testid={`portfolio-option-mobile-${p.id}`}>
                                    {p.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                )}

                {/* Drawer nav — primary items */}
                <nav className="flex-1 overflow-y-auto py-2">
                    {engineMode && drawerItem(ENGINE_ITEM)}
                    {engineMode && isEngineRoute(location.pathname) && (
                        <div className="px-3 py-2">
                            <LanguageSelect testid="engine-language-select-mobile" />
                        </div>
                    )}
                    {engineMode && (
                        <button
                            type="button"
                            onClick={() => setPersonalOpen((o) => !o)}
                            aria-expanded={personalOpen}
                            aria-controls="personal-nav-mobile"
                            data-testid="mobile-nav-personal-toggle"
                            className="w-full flex items-center gap-3 px-3 py-2.5 min-h-[44px] text-[14.5px] text-left border-l-2 border-transparent text-soft_muted hover:text-graphite hover:bg-stone transition-all"
                        >
                            <Briefcase className="w-4 h-4 shrink-0" strokeWidth={1.5} aria-hidden="true" />
                            {engineT("shell.personal")}
                            {quietPersonal && <span className="text-xs text-soft_muted/80">{engineT("shell.optional")}</span>}
                            <ChevronDown className={`w-4 h-4 ml-auto shrink-0 transition-transform${personalOpen ? " rotate-180" : ""}`} strokeWidth={1.5} aria-hidden="true" />
                        </button>
                    )}
                    {/* Siempre en el DOM (oculto si esta plegado): aria-controls tiene que apuntar a algo. */}
                    <div id="personal-nav-mobile" hidden={engineMode && !personalOpen} className={engineMode ? "pl-3" : undefined}>
                        {!shellPending && navItems.map(drawerItem)}
                    </div>

                    {/* Divider before secondary items */}
                    <div className="my-2 mx-4 border-t border-soft_border" />

                    {!shellPending && sideItems.map((it) => (
                        <NavLink
                            key={it.to}
                            to={it.to}
                            data-testid={`mobile-${it.testid}`}
                            className={drawerNavClass}
                            onClick={() => { hapticTap(); closeDrawer(); }}
                        >
                            <it.icon className="w-4 h-4 shrink-0" strokeWidth={1.5} aria-hidden="true" />
                            {itemLabel(it)}
                            {it.to === "/app/help" && user?.role === "admin" && adminPending > 0 && (
                                <span className="ml-auto text-[10px] font-bold bg-wine text-bone rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 leading-none">
                                    {adminPending > 99 ? "99+" : adminPending}
                                </span>
                            )}
                        </NavLink>
                    ))}

                    {user?.role === "admin" && (
                        <>
                            <NavLink
                                to="/admin"
                                data-testid="mobile-nav-admin"
                                className={drawerNavClass}
                                onClick={() => { hapticTap(); closeDrawer(); }}
                            >
                                <Shield className="w-4 h-4 shrink-0" strokeWidth={1.5} aria-hidden="true" />
                                Admin
                            </NavLink>
                            <NavLink
                                to="/admin/blog"
                                data-testid="mobile-nav-admin-blog"
                                className={drawerNavClass}
                                onClick={() => { hapticTap(); closeDrawer(); }}
                            >
                                <Newspaper className="w-4 h-4 shrink-0" strokeWidth={1.5} aria-hidden="true" />
                                Gestion Blog
                            </NavLink>
                            <NavLink
                                to="/admin/campaigns"
                                data-testid="mobile-nav-admin-campaigns"
                                className={drawerNavClass}
                                onClick={() => { hapticTap(); closeDrawer(); }}
                            >
                                <Send className="w-4 h-4 shrink-0" strokeWidth={1.5} aria-hidden="true" />
                                Campañas
                            </NavLink>
                            <NavLink
                                to="/admin/announcements"
                                data-testid="mobile-nav-admin-announcements"
                                className={drawerNavClass}
                                onClick={() => { hapticTap(); closeDrawer(); }}
                            >
                                <Megaphone className="w-4 h-4 shrink-0" strokeWidth={1.5} aria-hidden="true" />
                                Anuncios
                            </NavLink>
                            <NavLink
                                to="/admin/coupons"
                                data-testid="mobile-nav-admin-coupons"
                                className={drawerNavClass}
                                onClick={() => { hapticTap(); closeDrawer(); }}
                            >
                                <Tag className="w-4 h-4 shrink-0" strokeWidth={1.5} aria-hidden="true" />
                                Cupones
                            </NavLink>
                            <NavLink
                                to="/admin/influencers"
                                data-testid="mobile-nav-admin-influencers"
                                className={drawerNavClass}
                                onClick={() => { hapticTap(); closeDrawer(); }}
                            >
                                <Users className="w-4 h-4 shrink-0" strokeWidth={1.5} aria-hidden="true" />
                                Influencers
                            </NavLink>
                        </>
                    )}
                </nav>

                {/* Drawer footer — user info */}
                <div className="px-4 py-4 border-t border-soft_border flex items-center gap-3">
                    <Avatar className="w-9 h-9 bg-petrol text-bone shrink-0">
                        <AvatarFallback className="bg-petrol text-bone text-sm">
                            {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
                        </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                        <p className="text-sm font-medium text-graphite truncate">{user?.name}</p>
                        <p className="text-xs text-soft_muted truncate">{user?.email}</p>
                    </div>
                    <button
                        className="ml-auto flex items-center justify-center w-11 h-11 rounded-md text-soft_muted hover:text-wine hover:bg-soft_border transition-colors"
                        onClick={() => { logout(); navigate("/"); }}
                        aria-label="Cerrar sesión"
                        data-testid="mobile-logout"
                    >
                        <LogOut className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" />
                    </button>
                </div>
            </div>

            {/* ── Desktop sidebar ── */}
            <aside className="hidden md:flex flex-col w-60 shrink-0 border-r border-soft_border bg-stone px-4 py-6 sticky top-0 h-screen">
                <div className="px-2 mb-8">
                    <Logo size="md" />
                </div>
                <nav className="flex-1 space-y-0.5">
                    {engineMode && desktopItem(ENGINE_ITEM)}
                    {engineMode && isEngineRoute(location.pathname) && (
                        <div className="px-3 py-2">
                            <LanguageSelect />
                        </div>
                    )}
                    {engineMode && (
                        <button
                            type="button"
                            onClick={() => setPersonalOpen((o) => !o)}
                            aria-expanded={personalOpen}
                            aria-controls="personal-nav-desktop"
                            data-testid="nav-personal-toggle"
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-[14.5px] text-left text-soft_muted hover:text-graphite hover:bg-soft_border/40 transition-all"
                        >
                            <Briefcase className="w-4 h-4 shrink-0" strokeWidth={1.5} aria-hidden="true" />
                            {engineT("shell.personal")}
                            {quietPersonal && <span className="text-xs text-soft_muted/80">{engineT("shell.optional")}</span>}
                            <ChevronDown className={`w-4 h-4 ml-auto shrink-0 transition-transform${personalOpen ? " rotate-180" : ""}`} strokeWidth={1.5} aria-hidden="true" />
                        </button>
                    )}
                    <div id="personal-nav-desktop" hidden={engineMode && !personalOpen} className={engineMode ? "pl-3 space-y-0.5" : "space-y-0.5"}>
                        {!shellPending && navItems.map(desktopItem)}
                    </div>
                </nav>
                <div className="space-y-0.5 mt-6 pt-6 border-t border-soft_border">
                    {!shellPending && sideItems.map((it) => (
                        <NavLink
                            key={it.to}
                            to={it.to}
                            data-testid={it.testid}
                            className={desktopNavClass}
                        >
                            <it.icon className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" />
                            {itemLabel(it)}
                            {it.to === "/app/help" && user?.role === "admin" && adminPending > 0 && (
                                <span className="ml-auto text-[10px] font-bold bg-wine text-bone rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 leading-none">
                                    {adminPending > 99 ? "99+" : adminPending}
                                </span>
                            )}
                        </NavLink>
                    ))}
                    {user?.role === "admin" && (
                        <>
                            <NavLink
                                to="/admin"
                                data-testid="nav-admin"
                                className={desktopNavClass}
                            >
                                <Shield className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" />
                                Admin
                            </NavLink>
                            <NavLink
                                to="/admin/blog"
                                data-testid="nav-admin-blog"
                                className={desktopNavClass}
                            >
                                <Newspaper className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" />
                                Gestion Blog
                            </NavLink>
                            <NavLink
                                to="/admin/campaigns"
                                data-testid="nav-admin-campaigns"
                                className={desktopNavClass}
                            >
                                <Send className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" />
                                Campañas
                            </NavLink>
                            <NavLink
                                to="/admin/announcements"
                                data-testid="nav-admin-announcements"
                                className={desktopNavClass}
                            >
                                <Megaphone className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" />
                                Anuncios
                            </NavLink>
                            <NavLink
                                to="/admin/coupons"
                                data-testid="nav-admin-coupons"
                                className={desktopNavClass}
                            >
                                <Tag className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" />
                                Cupones
                            </NavLink>
                            <NavLink
                                to="/admin/influencers"
                                data-testid="nav-admin-influencers"
                                className={desktopNavClass}
                            >
                                <Users className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" />
                                Influencers
                            </NavLink>
                        </>
                    )}
                </div>
            </aside>

            {/* ── Main content column ── */}
            <div className="app-content-col flex-1 flex flex-col min-w-0">

                {/* ── Header ── */}
                <header className="app-header sticky top-0 z-30 bg-stone/95 backdrop-blur-sm border-b border-soft_border px-4 md:px-8 py-3 flex items-center gap-4">

                    {/* Hamburger button — non-native mobile only (native uses BottomNav "Mas") */}
                    {!isNative() && (
                        <button
                            className="md:hidden p-1.5 rounded-md text-soft_muted hover:text-graphite hover:bg-soft_border/40 transition-colors"
                            onClick={() => setMobileMenuOpen(true)}
                            aria-label="Abrir menú"
                            data-testid="hamburger-button"
                        >
                            <Menu className="w-5 h-5" strokeWidth={1.5} />
                        </button>
                    )}
                    {/* Logo in header on native mobile — branding instead of hamburger */}
                    {isNative() && (
                        <span className="md:hidden">
                            <Logo size="sm" />
                        </span>
                    )}

                    {/* Portfolio selector — compact, native mobile header only */}
                    {isNative() && portfolios.length > 0 && (
                        <div className="md:hidden flex-1 min-w-0 max-w-[180px]">
                            <Select value={selectedPortfolio} onValueChange={onPortfolioChange}>
                                <SelectTrigger
                                    className="h-8 text-xs bg-stone border-soft_border truncate"
                                    data-testid="portfolio-selector-header-native"
                                >
                                    <SelectValue placeholder="Cartera" />
                                </SelectTrigger>
                                <SelectContent>
                                    {portfolios.map((p) => (
                                        <SelectItem key={p.id} value={p.id} data-testid={`portfolio-option-header-native-${p.id}`}>
                                            {p.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    {/* Portfolio selector — desktop only */}
                    {!engineChrome && (
                    <div className="hidden md:block w-64">
                        <Select value={selectedPortfolio} onValueChange={onPortfolioChange}>
                            <SelectTrigger className="bg-stone border-soft_border" data-testid="portfolio-selector">
                                <SelectValue placeholder="Selecciona cartera" />
                            </SelectTrigger>
                            <SelectContent>
                                {portfolios.length === 0 && (
                                    <SelectItem value="none" disabled>
                                        No tienes carteras aún
                                    </SelectItem>
                                )}
                                {portfolios.map((p) => (
                                    <SelectItem key={p.id} value={p.id} data-testid={`portfolio-option-${p.id}`}>
                                        {p.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    )}

                    {/* Search — desktop always visible, mobile expandable icon (not in the Engine: it searches personal holdings) */}
                    {!engineChrome && (
                    <div className="flex-1 max-w-md relative hidden lg:block">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-soft_muted" strokeWidth={1.5} />
                        <Input
                            placeholder="Buscar activo, ticker, ISIN…"
                            className="pl-9 bg-stone border-soft_border focus-visible:ring-petrol"
                            data-testid="header-search"
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    navigate(`/app/holdings?q=${encodeURIComponent(e.currentTarget.value)}`);
                                }
                            }}
                        />
                    </div>
                    )}

                    {/* Mobile search — icon toggles inline input */}
                    {!engineChrome && (
                    <div className="lg:hidden flex items-center">
                        {mobileSearchOpen ? (
                            <div className="flex items-center gap-2">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-soft_muted" strokeWidth={1.5} />
                                    <Input
                                        autoFocus
                                        placeholder="Buscar activo, ticker, ISIN…"
                                        className="pl-9 bg-stone border-soft_border focus-visible:ring-petrol w-48 sm:w-64 text-base"
                                        data-testid="header-search-mobile"
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                navigate(`/app/holdings?q=${encodeURIComponent(e.currentTarget.value)}`);
                                                setMobileSearchOpen(false);
                                            }
                                            if (e.key === "Escape") {
                                                setMobileSearchOpen(false);
                                            }
                                        }}
                                        onBlur={() => setMobileSearchOpen(false)}
                                    />
                                </div>
                            </div>
                        ) : (
                            <button
                                className="p-2.5 rounded-md text-soft_muted hover:text-graphite hover:bg-soft_border/40 transition-colors"
                                onClick={() => setMobileSearchOpen(true)}
                                aria-label="Buscar"
                                data-testid="mobile-search-toggle"
                            >
                                <Search className="w-5 h-5" strokeWidth={1.5} />
                            </button>
                        )}
                    </div>
                    )}

                    {/* User dropdown — always visible */}
                    <div className="ml-auto flex items-center gap-3">
                        {/* Las notificaciones son de la cartera personal (alertas de precio, VL): en
                            el Engine, la campana solo si hay alguna. */}
                        {(!engineChrome || notifications.length > 0) && (
                        <Popover>
                            <PopoverTrigger asChild>
                                <div className="relative">
                                    <button
                                        className="p-1.5 rounded-md text-soft_muted hover:text-graphite hover:bg-soft_border/40 transition-colors"
                                        aria-label="Notificaciones"
                                        data-testid="nav-reminder-bell"
                                    >
                                        <Bell className="w-5 h-5" strokeWidth={1.5} />
                                    </button>
                                    {unreadCount > 0 && (
                                        <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-wine ring-2 ring-bone pointer-events-none" />
                                    )}
                                </div>
                            </PopoverTrigger>
                            <PopoverContent
                                align="end"
                                sideOffset={8}
                                className="w-[calc(100vw-2rem)] max-w-sm p-0 bg-bone border-soft_border rounded-xl shadow-lg overflow-hidden max-h-[420px] flex flex-col"
                                style={{ marginRight: "env(safe-area-inset-right, 0px)" }}
                                data-testid="nav-reminder-panel"
                            >
                                <div className="flex items-center justify-between px-4 py-3 border-b border-soft_border">
                                    <p className="text-sm font-semibold text-graphite">Notificaciones</p>
                                </div>
                                <div className="flex-1 overflow-y-auto divide-y divide-soft_border">
                                    {notifications.length === 0 ? (
                                        <div className="px-4 py-8 text-center">
                                            <p className="text-sm text-soft_muted">Sin notificaciones pendientes.</p>
                                        </div>
                                    ) : (
                                        notifications.map((n) => (
                                            <div
                                                key={n.id}
                                                className={`px-4 py-3 transition-colors ${n.read ? "bg-bone" : "bg-stone"}`}
                                            >
                                                <div className="flex items-start gap-3">
                                                    <span className="text-base leading-none mt-0.5 shrink-0">
                                                        {n.type === "announcement" ? "📣" : n.type === "price_alert" ? "📈" : "🏦"}
                                                    </span>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-graphite truncate">{n.title}</p>
                                                        <p className="text-xs text-soft_muted mt-0.5">{n.body}</p>
                                                        <p className="text-xs text-soft_muted mt-0.5">
                                                            {daysSince(n.created_at)}
                                                        </p>
                                                        {n.type === "announcement" && n.url ? (
                                                            <button
                                                                className="mt-2 text-xs text-petrol hover:text-petrol-hover underline underline-offset-2 transition-colors"
                                                                onClick={() => {
                                                                    markRead(n.id);
                                                                    openUrl(n.url);
                                                                }}
                                                            >
                                                                {n.cta_label || "Ver más"}
                                                            </button>
                                                        ) : n.portfolio_id && n.holding_id ? (
                                                            <button
                                                                className="mt-2 text-xs text-petrol hover:text-petrol-hover underline underline-offset-2 transition-colors"
                                                                onClick={() => {
                                                                    markRead(n.id);
                                                                    navigate(`/app/portfolios/${n.portfolio_id}?editHolding=${n.holding_id}`);
                                                                }}
                                                            >
                                                                Actualizar VL
                                                            </button>
                                                        ) : !n.read ? (
                                                            <button
                                                                className="mt-2 text-xs text-soft_muted hover:text-graphite underline underline-offset-2 transition-colors"
                                                                onClick={() => markRead(n.id)}
                                                            >
                                                                Marcar leída
                                                            </button>
                                                        ) : null}
                                                    </div>
                                                    {!n.read && (
                                                        <span className="w-1.5 h-1.5 rounded-full bg-wine shrink-0 mt-1.5" />
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                                {unreadCount > 0 && (
                                    <div className="border-t border-soft_border px-4 py-2.5">
                                        <button
                                            className="w-full text-xs text-soft_muted hover:text-graphite transition-colors text-center"
                                            onClick={markAllRead}
                                            data-testid="reminder-mark-all-read"
                                        >
                                            Marcar todo como leído
                                        </button>
                                    </div>
                                )}
                            </PopoverContent>
                        </Popover>
                        )}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button className="flex items-center gap-2" aria-label="Menú de perfil" data-testid="profile-menu">
                                    <Avatar className="w-9 h-9 bg-petrol text-bone">
                                        <AvatarFallback className="bg-petrol text-bone text-sm">
                                            {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
                                        </AvatarFallback>
                                    </Avatar>
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                                <div className="px-2 py-2">
                                    <p className="text-sm font-medium text-graphite">{user?.name}</p>
                                    <p className="text-xs text-soft_muted truncate">{user?.email}</p>
                                </div>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => navigate("/app/settings")} data-testid="menu-settings">
                                    <SettingsIcon className="w-4 h-4 mr-2" strokeWidth={1.5} /> Ajustes
                                </DropdownMenuItem>
                                {!engineChrome && (
                                    <DropdownMenuItem onClick={() => navigate("/app/billing")} data-testid="menu-billing">
                                        <CreditCard className="w-4 h-4 mr-2" strokeWidth={1.5} /> Facturación
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    className="text-wine focus:text-wine"
                                    onClick={() => {
                                        logout();
                                        navigate("/");
                                    }}
                                    data-testid="menu-logout"
                                >
                                    <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} /> Cerrar sesión
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </header>

                {!engineChrome && <TrialBanner />}

                {announcement && (
                    <div
                        className={`px-4 md:px-8 py-2.5 border-b border-soft_border flex items-center gap-3 ${
                            announcement.style === "promo" ? "bg-petrol text-bone" : "bg-petrol/10 text-graphite"
                        }`}
                        data-testid="announcement-banner"
                    >
                        <Megaphone className="w-4 h-4 shrink-0" strokeWidth={1.5} />
                        <p className="text-sm flex-1 min-w-0 truncate">
                            <span className="font-semibold">{announcement.title}</span>
                            {announcement.body && (
                                <span className={announcement.style === "promo" ? "opacity-80" : "text-soft_muted"}>
                                    {" — "}{announcement.body}
                                </span>
                            )}
                        </p>
                        {announcement.cta_url && (
                            <button
                                onClick={() => openUrl(announcement.cta_url)}
                                className={`shrink-0 text-xs font-semibold px-3 py-1.5 rounded-lg transition ${
                                    announcement.style === "promo"
                                        ? "bg-bone text-petrol hover:bg-stone"
                                        : "bg-petrol text-bone hover:bg-petrol-hover"
                                }`}
                                data-testid="announcement-cta"
                            >
                                {announcement.cta_label || "Ver más"}
                            </button>
                        )}
                        <button
                            onClick={dismissAnnouncement}
                            className="shrink-0 p-1 rounded-md opacity-70 hover:opacity-100 transition"
                            aria-label="Descartar anuncio"
                            data-testid="announcement-dismiss"
                        >
                            <X className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                    </div>
                )}

                <main
                    key={location.pathname}
                    className={`flex-1 px-4 md:px-10 py-8 max-w-[1400px] w-full mx-auto${isNative() ? " page-transition" : ""}`}
                    data-testid="app-main"
                >
                    {/* Sin texto: hasta que el idioma del Engine esta en su sitio no hay en que idioma escribirlo. */}
                    {languageSettled ? <Outlet context={{ engine }} /> : <div className="min-h-[50vh]" aria-busy="true" data-testid="engine-language-pending" />}
                </main>

                <DisclaimerBar />
            </div>

            {isNative() && <BottomNav onMore={() => setMobileMenuOpen(true)} />}
        </div>
    );
}
